<template>
  <div>
    <!-- ── Initiate payment modal ─────────────────────────────────────────── -->
    <b-modal
      id="modal-initiate-payments"
      ref="initiateModal"
      centered
      no-close-on-backdrop
      no-close-on-esc
      size="xl"
      hide-footer
      hide-header
    >
      <template #default="{}">
        <div class="modal-inner">
          <!-- Header -->
          <div class="modal-head">
            <div class="d-flex align-items-center" style="gap: 10px">
              <div class="modal-icon modal-icon-warning">
                <feather-icon icon="SunriseIcon" size="16" />
              </div>
              <div>
                <div class="modal-title">
                  Initiate payment — {{ selecteds.length }} beneficiar{{ selecteds.length === 1 ? 'y' : 'ies' }}
                </div>
                <div class="modal-sub">
                  Review the list below before confirming. Payments cannot be recalled once initiated.
                </div>
              </div>
            </div>
            <feather-icon
              icon="XIcon"
              size="16"
              class="cursor-pointer text-secondary"
              @click="$bvModal.hide('modal-initiate-payments')"
            />
          </div>

          <div class="modal-body-inner">
            <!-- Summary -->
            <b-row class="mb-1">
              <b-col cols="6" md="3">
                <div class="weight-card wc-blue">
                  <div class="wc-label">Beneficiaries</div>
                  <div class="wc-value">{{ selecteds.length }}</div>
                </div>
              </b-col>
              <b-col cols="6" md="3">
                <div class="weight-card wc-amber">
                  <div class="wc-label">Gross value</div>
                  <div class="wc-value">{{ fmtMoney(selectedTotals.value, selectedCurrency) }}</div>
                </div>
              </b-col>
              <b-col cols="6" md="3">
                <div class="weight-card wc-red">
                  <div class="wc-label">Deductions</div>
                  <div class="wc-value">{{ fmtMoney(selectedTotals.deductions, selectedCurrency) }}</div>
                </div>
              </b-col>
              <b-col cols="6" md="3">
                <div class="weight-card wc-green">
                  <div class="wc-label">Net payable</div>
                  <div class="wc-value">{{ fmtMoney(selectedTotals.net, selectedCurrency) }}</div>
                </div>
              </b-col>
            </b-row>

            <div class="section-head">Beneficiaries in this run</div>
            <b-table
              ref="modalRecords"
              striped
              hover
              small
              class="position-relative payments-table"
              :items="selecteds"
              responsive
              :fields="modalColumns"
              primary-key="sale_id"
              show-empty
              empty-text="No records selected"
              sticky-header="360px"
            >
              <template #cell(name)="data">
                <div class="d-flex align-items-center" style="gap: 8px">
                  <b-avatar size="26" :text="avatarText(data.item.name)" variant="light-primary" class="flex-shrink-0" />
                  <span style="font-size: 12px; font-weight: 500">{{ data.item.name }}</span>
                </div>
              </template>

              <template #cell(market)="data">
                <span v-if="data.item.market.code !== null">{{ data.item.market.code.toUpperCase() }}: {{ data.item.market.name.toUpperCase() }}</span>
                <span v-else>{{ data.item.market.name.toUpperCase() }}</span>
              </template>

              <template #cell(account_number)="data">
                <span class="font-monospace" style="font-size: 12px">{{ data.item.account.number }}</span>
              </template>

              <template #cell(value)="data">
                {{ fmtMoney(data.item.value, data.item.account.abbreviation) }}
              </template>

              <template #cell(paid)="data">
                {{ fmtMoney(data.item.paid, data.item.account.abbreviation) }}
              </template>

              <template #cell(deductions)="data">
                {{ fmtMoney(data.item.loan, data.item.account.abbreviation) }}
              </template>

              <template #cell(net)="data">
                <span class="text-success font-weight-bold">{{ fmtMoney(netOf(data.item), data.item.account.abbreviation) }}</span>
              </template>
            </b-table>

            <small v-if="serverErrors" class="text-danger d-block mt-1">
              {{ Object.values(serverErrors).flat().join(' ') }}
            </small>
          </div>

          <!-- Footer -->
          <div class="modal-foot">
            <span class="foot-hint">Double-check bank accounts before confirming.</span>
            <b-button
              size="sm"
              variant="outline-secondary"
              :disabled="saving"
              @click="$bvModal.hide('modal-initiate-payments')"
            >
              Cancel
            </b-button>
            <b-button
              size="sm"
              variant="warning"
              :disabled="saving || selecteds.length === 0"
              @click="initiatePayments"
            >
              <b-spinner v-if="saving" small class="mr-50" />
              {{ saving ? 'Initiating...' : 'Confirm & Initiate Pay' }}
            </b-button>
          </div>
        </div>
      </template>
    </b-modal>
    <!-- ./Initiate payment modal -->

    <!-- ── Page header ─────────────────────────────────────────────────────── -->
    <div class="page-section-header mb-1">
      <div class="d-flex align-items-center" style="gap: 12px">
        <div class="page-header-icon">
          <feather-icon icon="CreditCardIcon" size="20" />
        </div>
        <div>
          <h5 class="mb-25">Grower Payments</h5>
          <small class="text-muted">
            Review payable sales by bank and currency, then initiate payment runs to grower accounts
          </small>
        </div>
      </div>
    </div>

    <!-- ── Filters ─────────────────────────────────────────────────────────── -->
    <b-card no-body class="filter-card mb-1">
      <b-card-body>
        <b-row>
          <b-col
            cols="12"
            md="4"
            class="mb-md-0 mb-2"
          >
            <b-form-group
              label-for="bank_id"
              label="Pay Bank"
            >
              <validation-provider
                #default="{ errors }"
                name="bank_id"
                rules=""
              >
                <b-form-select
                  id="bank_id"
                  v-model="bank_id"
                  size="sm"
                  name="bank_id"
                  :state="errors.length > 0 ? false : null"
                  @change="filterSales"
                >
                  <b-form-select-option :value="null">
                    Select...
                  </b-form-select-option>
                  <b-form-select-option
                    v-for="bank in banks"
                    :key="bank.id"
                    :value="bank.id"
                  >
                    {{ bank.name }}
                  </b-form-select-option>
                </b-form-select>
                <small class="text-danger">{{ errors[0] }}</small>
                <small
                  v-if="serverErrors && serverErrors.bank_id"
                  class="text-danger"
                >{{
                  serverErrors.bank_id[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="2"
            class="mb-md-0 mb-2"
          >
            <b-form-group
              label-for="currency_id"
              label="Currency"
            >
              <validation-provider
                #default="{ errors }"
                name="currency_id"
                rules=""
              >
                <b-form-select
                  id="currency_id"
                  v-model="currency_id"
                  size="sm"
                  name="currency_id"
                  :state="errors.length > 0 ? false : null"
                  @change="filterSales"
                >
                  <b-form-select-option :value="null">
                    Select...
                  </b-form-select-option>
                  <b-form-select-option
                    v-for="currency in currencies"
                    :key="currency.id"
                    :value="currency.id"
                  >{{ currency.abbreviation }}</b-form-select-option>
                </b-form-select>
                <small class="text-danger">{{ errors[0] }}</small>
                <small
                  v-if="serverErrors && serverErrors.currency_id"
                  class="text-danger"
                >{{ serverErrors.currency_id[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="6"
            class="mb-md-0 mb-2"
          >
            <location-filters
              size="sm"
              align="horizontal"
              @filter="changeFilter"
            />
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>
    <!-- ./Filters -->

    <!-- ── Metric cards ────────────────────────────────────────────────────── -->
    <b-row v-if="payables.length" class="mb-1">
      <b-col cols="6" md="3">
        <b-card class="metric-card metric-card-blue mb-1 mb-md-0">
          <div class="metric-icon metric-icon-blue">
            <feather-icon icon="FileTextIcon" size="16" />
          </div>
          <div class="metric-label">Payable sales</div>
          <div class="metric-value text-primary">{{ payables.length.toLocaleString() }}</div>
        </b-card>
      </b-col>
      <b-col cols="6" md="3">
        <b-card class="metric-card metric-card-amber mb-1 mb-md-0">
          <div class="metric-icon metric-icon-amber">
            <feather-icon icon="DollarSignIcon" size="16" />
          </div>
          <div class="metric-label">Total value</div>
          <div class="metric-value text-warning">{{ fmtMoney(totals.value, currencyAbbr) }}</div>
        </b-card>
      </b-col>
      <b-col cols="6" md="3">
        <b-card class="metric-card metric-card-red mb-1 mb-md-0">
          <div class="metric-icon metric-icon-red">
            <feather-icon icon="MinusCircleIcon" size="16" />
          </div>
          <div class="metric-label">Deductions</div>
          <div class="metric-value text-danger">{{ fmtMoney(totals.deductions, currencyAbbr) }}</div>
        </b-card>
      </b-col>
      <b-col cols="6" md="3">
        <b-card class="metric-card metric-card-green mb-0">
          <div class="metric-icon metric-icon-green">
            <feather-icon icon="CheckCircleIcon" size="16" />
          </div>
          <div class="metric-label">Net payable</div>
          <div class="metric-value text-success">{{ fmtMoney(totals.net, currencyAbbr) }}</div>
        </b-card>
      </b-col>
    </b-row>

    <!-- ── Table Container Card ────────────────────────────────────────────── -->
    <b-card
      no-body
      class="mb-0"
    >
      <!-- Bulk action bar -->
      <div v-if="selecteds.length > 0" class="bulk-bar">
        <feather-icon icon="CheckSquareIcon" size="14" class="mr-50" />
        <span>
          {{ selecteds.length }} beneficiar{{ selecteds.length === 1 ? 'y' : 'ies' }} selected
          · Net {{ fmtMoney(selectedTotals.net, selectedCurrency) }}
        </span>
        <b-button
          v-b-modal.modal-initiate-payments
          variant="warning"
          size="sm"
        >
          <span class="align-middle">Initiate Pay</span>
          <feather-icon
            icon="SunriseIcon"
            class="ml-50"
          />
        </b-button>
        <b-button size="sm" variant="outline-secondary" @click="selecteds = []">Clear selection</b-button>
      </div>
      <div v-else class="m-2 d-flex align-items-center justify-content-end">
        <small class="text-muted">Select beneficiaries below to initiate a payment run</small>
      </div>

      <!-- Not-yet-filtered placeholder -->
      <div v-if="1 === 2" class="empty-state">
        <feather-icon icon="FilterIcon" size="30" class="empty-state-icon" />
        <div class="empty-state-title">Choose a bank, currency and market location</div>
        <div class="empty-state-sub">Payable sales will appear here once all three filters above are selected.</div>
      </div>

      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <label>Show</label>
            <v-select v-model="perPage" :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" :options="perPageOptions"
                      :clearable="false" class="per-page-selector d-inline-block mx-50 select-size-sm"
            />
            <label>entries</label>
          </b-col>
          <!-- ./Per Page -->

          <!-- Search -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input v-model="searchQuery" class="d-inline-block mr-1" placeholder="Search..." size="sm" />
            </div>
          </b-col>
          <!-- ./Search -->
        </b-row>
        <!-- ./Table Top -->
      </div>

      <!-- Table -->
      <b-table
        ref="records"
        striped
        hover
        small
        class="position-relative payments-table"
        :items="fetch"
        responsive
        :fields="columns"
        primary-key="sale_id"
        show-empty
        empty-text="No payable sales found for this selection"
        :busy="loading"
        sticky-header="640px"
      >
        <template #table-busy>
          <div class="text-center text-muted my-3">
            <b-spinner class="align-middle mr-50" small />
            <span>Loading initiated payments...</span>
          </div>
        </template>

        <template #head(id)>
          <div style="display: flex; align-items: center; justify-content: center">
            <input
              type="checkbox"
              :checked="allSelected"
              style="width: 15px; height: 15px; cursor: pointer; accent-color: #ef9f27"
              @change="toggleAll"
            />
          </div>
        </template>

        <template #cell(id)="data">
          <div style="display: flex; align-items: center; justify-content: center">
            <input
              type="checkbox"
              :checked="selecteds.includes(data.item)"
              style="width: 15px; height: 15px; cursor: pointer; accent-color: #ef9f27"
              @change="toggleRow(data.item)"
            />
          </div>
        </template>

        <template #cell(name)="data">
          <div class="d-flex align-items-center" style="gap: 8px">
            <span style="font-size: 12px; font-weight: 500">{{ data.item.beneficiary.name }}</span>
          </div>
        </template>

        <template #cell(market)="data">
          <span v-if="data.item.payable.market.code !== null">{{ data.item.payable.market.code.toUpperCase() }}: {{ data.item.payable.market.name.toUpperCase() }}</span>
          <span v-else>{{ data.item.payable.market.name.toUpperCase() }}</span>
        </template>

        <template #cell(number)="data">
          {{ data.item.payable.number }}
        </template>

        <template #cell(account_name)="data">
          {{ data.item.account.name }}
        </template>

        <template #cell(account_number)="data">
          <span class="font-monospace text-muted" style="font-size: 12px">{{ data.item.account.number }}</span>
        </template>

        <template #cell(amount)="data">
          {{ fmtMoney(data.item.amount, data.item.account.abbreviation) }}
        </template>

        <template #cell(recoveries)="data">
          {{ fmtMoney(data.item.recoveries.reduce((sum, recovery) => sum + Number(recovery.amount), 0), data.item.account.abbreviation) }}
        </template>

        <template #cell(net)="data">
          {{ fmtMoney(data.item.amount - data.item.recoveries.reduce((sum, recovery) => sum + Number(recovery.amount), 0), data.item.account.abbreviation) }}
        </template>

        <template #cell(initiatedDate)="data">
          <span style="font-size: 12px">{{ data.item.initiatedDate | moment("DD/MM/YYYY") }}</span>
        </template>

        <template #cell(deposits)="data">
          {{ fmtMoney(data.item.deposits.reduce((sum, deposit) => sum + Number(deposit.amount), 0), data.item.account.abbreviation) }}
        </template>

        <template #cell(paidDate)="data">
          <span v-if="data.item.deposits.length > 0" style="font-size: 12px">{{ data.item.deposits[data.item.deposits.length - 1].paidDate | moment("DD/MM/YYYY") }}</span>
        </template>

        <template #cell(bank)="data">
          <span v-if="data.item.deposits.length > 0 && data.item.deposits[data.item.deposits.length - 1].bank !== null">
            {{ data.item.deposits[data.item.deposits.length - 1].bank  }}
          </span>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted">Showing {{ from }} to {{ to }} of {{ totalRecords }} entries</span>
          </b-col>
          <!-- Pagination -->
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <b-pagination v-model="currentPage" :total-rows="totalRecords" :per-page="perPage" first-number last-number
                          class="mb-0 mt-1 mt-sm-0" prev-class="prev-item" next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>


    </b-card>
    <!-- ./Table Container Card -->
  </div>
  
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import {
    BCard, BCardHeader, BCardBody, BCardText,
    BRow, BCol,
    BForm, BFormGroup, BFormInput, BFormCheckbox, BDropdown, BDropdownItem,
    BButton, BSpinner, BFormSelect, BFormSelectOption,
    BTable, BPagination, BBadge, BAvatar,
    VBModal,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { required } from '@validations'
import store from '@/store'
import {
    ref, onUnmounted, onMounted, computed,
} from '@vue/composition-api'
import depositsStoreModule from '@/views/cromis/payments/deposit/depositsStoreModule'
import LocationFilters from '@core/components/cromis/filters/LocationFilters.vue'
import { avatarText } from '@core/utils/filter'
import useDepositsList from '@/views/cromis/payments/deposit/useDepositsList'

export default {
    components: {
        BCard,
        BCardHeader,
        BCardBody,
        BCardText,
        BRow,
        BCol,
        BForm,
        BFormGroup,
        BFormInput,
        BFormCheckbox,
        BDropdown,
        BDropdownItem,
        vSelect,
        BButton,
        BSpinner,
        BFormSelect,
        BFormSelectOption,
        BTable,
        BPagination,
        BBadge,
        BAvatar,
        ValidationProvider,
        ValidationObserver,
        VBModal,
        LocationFilters,
    },
    directives: {
        'b-modal': VBModal,
    },
    props: {},
    setup(props, context) {
        const dataForm = ref(null)
        const saving = ref(false)
        const loading = ref(false)
        const serverErrors = ref(null)

        const banks = ref([])
        const currencies = ref([])
        const payables = ref([])
        const selecteds = ref([])

        const modalColumns = ref([
            {
                key: 'name', label: 'Beneficiary', sortable: false, thStyle: { width: '22%' },
            },
            { key: 'code', sortable: false },
            {
                key: 'market', label: 'Market Center', sortable: false, thStyle: { width: '20%' },
            },
            { key: 'saleNumber', label: 'Sale #', sortable: false },
            { key: 'account_number', label: 'Account', sortable: false },
            {
                key: 'value', sortable: false, thStyle: { width: '12%' }, thClass: 'text-right', tdClass: 'text-right',
            },
            {
                key: 'paid', sortable: false, thStyle: { width: '12%' }, thClass: 'text-right', tdClass: 'text-right',
            },
            {
                key: 'deductions', sortable: false, thStyle: { width: '10%' }, thClass: 'text-right', tdClass: 'text-right',
            },
            {
                key: 'net', label: 'Net', sortable: false, thStyle: { width: '10%' }, thClass: 'text-right', tdClass: 'text-right',
            },
        ])

        const CROMIS_GROUP_STORE_MODULE_NAME = 'cromis-payments'

        // Register module
        if (!store.hasModule(CROMIS_GROUP_STORE_MODULE_NAME)) store.registerModule(CROMIS_GROUP_STORE_MODULE_NAME, depositsStoreModule)

        // UnRegister on leave
        onUnmounted(() => {
            if (store.hasModule(CROMIS_GROUP_STORE_MODULE_NAME)) store.unregisterModule(CROMIS_GROUP_STORE_MODULE_NAME)
        })

        onMounted(async () => {
            await store.dispatch('cromis-payments/getBanks')
                .then(response => {
                    banks.value = response.data.banks
                })
                .catch(error => {
                    console.log(error)
                })

            await store.dispatch('cromis-payments/getCurrencies')
                .then(response => {
                    currencies.value = response.data.currencies
                })
                .catch(error => {
                    console.log(error)
                })
        })

        const {
            fetch,
            columns,
            perPage,
            currentPage,
            totalRecords,
            from,
            to,
            meta,
            perPageOptions,
            searchQuery,
            sortBy,
            isSortDirDesc,
            records,
            refetch,

            // Filters
            locationFilter,
            administrativeFilter,
            locationType,
            bank_id,
            currency_id,
            location_id,

            // UI
            // resolveUserRoleVariant,
            // resolveUserRoleIcon,
            resolveStatusVariant,
        } = useDepositsList()

        const changeFilter = filteredLocation => {
            location_id.value = filteredLocation !== null ? filteredLocation.id : null
            filterSales()
        }

        const filtersReady = computed(() => bank_id.value !== null && currency_id.value !== null && location_id.value !== null)

        const filterSales = async () => {
            if (filtersReady.value) {
                loading.value = true
                selecteds.value = []
                await store.dispatch('cromis-payments/getSales', { bank_id: bank_id.value, location_id: location_id.value, currency_id: currency_id.value })
                    .then(response => {
                        payables.value = response.data.payables

                        payables.value = payables.value.flatMap(payable => {
                            let totalLoan = 0
                            let totalRecovery = 0

                            payable.loans.forEach(loan => {
                                loan.transactions.forEach(transaction => {
                                    if (transaction.sign === '+') totalLoan += transaction.amount
                                })

                                loan.recoveries.forEach(recovery => {
                                    totalRecovery += recovery.amount
                                })
                            })

                            return payable.sales.map(sale => ({
                                id: payable.id,
                                type: 1,
                                code: payable.code,
                                number: payable.number,
                                name: payable.name,
                                sale_id: sale.id,
                                saleNumber: sale.number,
                                market: sale.market,
                                closedDate: sale.closedDate,
                                account: payable.account,
                                value: sale.value,
                                paid: sale.paid,
                                deduction: sale.ticketsDeductions,
                                loan: sale.value - sale.paid < 0
                                    ? (sale.paid - sale.value) + (totalLoan - totalRecovery)
                                    : (totalLoan - totalRecovery),
                            }))
                        })

                        loading.value = false
                    })
                    .catch(error => {
                        loading.value = false
                        console.log(error)
                    })
            } else {
                payables.value = []
            }
        }

        // ── Formatting helpers ───────────────────────────────────────────────
        const fmtMoney = (value, currency) => (value || 0).toLocaleString('en-US', {
            style: 'currency', currency: currency || 'USD', minimumFractionDigits: 3, maximumFractionDigits: 3,
        })

        const netOf = item => (item.value >= (item.loan + item.paid) ? item.value - item.loan - item.paid : 0)

        // ── Totals ────────────────────────────────────────────────────────────
        const currencyAbbr = computed(() => (payables.value.length ? payables.value[0].account.abbreviation : 'USD'))

        const totals = computed(() => payables.value.reduce((acc, item) => {
            acc.value += item.value
            acc.deductions += item.loan
            acc.net += netOf(item)
            return acc
        }, { value: 0, deductions: 0, net: 0 }))

        const selectedCurrency = computed(() => (selecteds.value.length ? selecteds.value[0].account.abbreviation : 'USD'))

        const selectedTotals = computed(() => selecteds.value.reduce((acc, item) => {
            acc.value += item.value
            acc.deductions += item.loan
            acc.net += netOf(item)
            return acc
        }, { value: 0, deductions: 0, net: 0 }))

        // ── Row / bulk selection ─────────────────────────────────────────────
        const allSelected = computed(() => payables.value.length > 0 && selecteds.value.length === payables.value.length)

        const toggleAll = () => {
            selecteds.value = allSelected.value ? [] : [...payables.value]
        }

        const toggleRow = item => {
            const idx = selecteds.value.indexOf(item)
            if (idx > -1) selecteds.value.splice(idx, 1)
            else selecteds.value.push(item)
        }

        const initiatePayments = async () => {
            saving.value = true
            serverErrors.value = null

            for (let i = 0; i < selecteds.value.length; i++) {
                await store.dispatch('cromis-payments/create', {
                    credited_type: 1,
                    beneficiary_id: selecteds.value[i].id,
                    payment_type: 1,
                    payable_id: selecteds.value[i].sale_id,
                    account_id: selecteds.value[i].account.id,
                    amount: parseFloat((selecteds.value[i].value - selecteds.value[i].paid).toFixed(3)),
                    deduction: parseFloat(selecteds.value[i].loan.toFixed(3)),
                })
                    .then(response => {
                    // console.log(response)
                    })
                    .catch(error => {
                        if (error.response.status === 422) {
                            serverErrors.value = error.response.data.errors
                        } else {
                            context.root.$swal({
                                icon: 'error',
                                title: 'Server Error',
                                text: 'Something went wrong. See tech support',
                                showConfirmButton: true,
                                customClass: {
                                    confirmButton: 'btn btn-danger',
                                },
                                buttonsStyling: false,
                            })
                        }
                    })
            }

            saving.value = false
            context.root.$bvModal.hide('modal-initiate-payments')

            context.root.$swal({
                icon: 'success',
                text: `Payments to all ${selecteds.value.length} beneficiaries have been initiated successfully!`,
                showConfirmButton: true,
                timer: 5000,
                customClass: {
                    confirmButton: 'btn btn-primary',
                },
                buttonsStyling: false,
            })

            selecteds.value = []

            filterSales()
        }

        return {
            //Table
            fetch,
            columns,
            perPage,
            currentPage,
            totalRecords,
            from,
            to,
            meta,
            perPageOptions,
            searchQuery,
            sortBy,
            isSortDirDesc,
            records,
            refetch,

            // Form
            dataForm,
            saving,
            loading,
            serverErrors,
            changeFilter,
            filterSales,
            filtersReady,

            // Validations
            required,
            banks,
            currencies,
            selecteds,
            bank_id,
            currency_id,
            payables,

            // Formatting / totals
            fmtMoney,
            netOf,
            avatarText,
            currencyAbbr,
            totals,
            selectedCurrency,
            selectedTotals,
            allSelected,
            toggleAll,
            toggleRow,

            // Table
            modalColumns,
            initiatePayments,
        }
    },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-select.scss';

.page-section-header {
  padding-bottom: 0.85rem;
  border-bottom: 0.5px solid #ebe9f1;

  .page-header-icon {
    width: 42px; height: 42px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    background: linear-gradient(135deg, #e6f1fb, #d9ecf9);
    color: #185fa5;
  }
}

.filter-card {
  border-radius: 0.6rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
}

// ── Metric cards ───────────────────────────────────────────────────────────
.metric-card {
  border-radius: 0.6rem;
  border-top: 3px solid transparent;
  box-shadow: 0 4px 18px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.05);
  transition: box-shadow 0.2s;

  &:hover { box-shadow: 0 8px 28px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.06); }

  ::v-deep .card-body { padding: 1rem 1.1rem; }

  &.metric-card-blue  { border-top-color: #185fa5; }
  &.metric-card-amber { border-top-color: #ef9f27; }
  &.metric-card-green { border-top-color: #639922; }
  &.metric-card-red   { border-top-color: #e24b4a; }

  .metric-icon {
    width: 34px; height: 34px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center; margin-bottom: 10px;
    &.metric-icon-blue  { background: #e6f1fb; color: #185fa5; }
    &.metric-icon-amber { background: #faeeda; color: #854f0b; }
    &.metric-icon-green { background: #eaf3de; color: #3b6d11; }
    &.metric-icon-red   { background: #fcebeb; color: #a32d2d; }
  }

  .metric-label {
    font-size: 11px; color: #b9b9c3;
    text-transform: uppercase; letter-spacing: 0.35px; margin-bottom: 5px;
  }
  .metric-value { font-size: 22px; font-weight: 500; line-height: 1.2; }
}

// ── Bulk bar ───────────────────────────────────────────────────────────────
.bulk-bar {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  padding: 9px 16px;
  background: #faeeda; border-bottom: 0.5px solid #fac775;
  font-size: 12px; color: #854f0b;

  span { margin-right: auto; }
}

// ── Empty state ──────────────────────────────────────────────────────────
.empty-state {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  text-align: center; padding: 48px 20px;
  .empty-state-icon { color: #d8d6de; margin-bottom: 10px; }
  .empty-state-title { font-size: 14px; font-weight: 500; color: #5e5873; }
  .empty-state-sub { font-size: 12px; color: #b9b9c3; margin-top: 4px; max-width: 360px; }
}

// ── Modal ──────────────────────────────────────────────────────────────────
.modal-inner { overflow: hidden; }

.modal-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 0.5px solid #ebe9f1; background: #fafafa;

  .modal-icon {
    width: 34px; height: 34px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    &.modal-icon-success { background: #eaf3de; color: #3b6d11; }
    &.modal-icon-danger  { background: #fcebeb; color: #a32d2d; }
    &.modal-icon-warning { background: #faeeda; color: #854f0b; }
  }

  .modal-title { font-size: 14px; font-weight: 600; color: #5e5873; }
  .modal-sub   { font-size: 11px; color: #b9b9c3; margin-top: 1px; }
}

.modal-body-inner { padding: 18px 18px 4px; }

.modal-foot {
  display: flex; align-items: center; justify-content: flex-end; gap: 8px;
  padding: 12px 18px;
  border-top: 0.5px solid #ebe9f1; background: #fafafa;
  .foot-hint { flex: 1; font-size: 11px; color: #b9b9c3; }
}

// ── Weight-style summary cards (reused inside modal) ────────────────────────
.weight-card {
  border-radius: 8px; padding: 11px 13px; margin-bottom: 10px;
  border-left: 3px solid transparent; background: #f8f8f8;
  &.wc-blue  { border-left-color: #185fa5; }
  &.wc-amber { border-left-color: #ef9f27; }
  &.wc-green { border-left-color: #639922; }
  &.wc-red   { border-left-color: #e24b4a; }
  .wc-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; color: #b9b9c3; margin-bottom: 4px; }
  .wc-value { font-size: 16px; font-weight: 500; color: #5e5873; }
}

.section-head {
  font-size: 11px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.5px; color: #b9b9c3;
  padding-bottom: 8px; border-bottom: 0.5px solid #ebe9f1; margin-bottom: 12px;
}

.payments-table { font-size: 13px; }

::v-deep table thead th:first-child,
::v-deep table tbody td:first-child {
  text-align: center; vertical-align: middle;
  padding-left: 12px; padding-right: 12px;
}
</style>
